import { getToday } from "../utils";
import { contatos, contatosNoDia, porRegiao, porPessoa, metaDe } from "./metas";
import { precisaJustificar, diasParado } from "./abordagem";
import { pendentesDeConferencia } from "./conferencia";
import { devidos } from "./pesquisa";

// ═══════════════════════════════════════════════════════════════
// RELATÓRIO DIÁRIO — o dia inteiro numa página
// ═══════════════════════════════════════════════════════════════
//
// Antes o "relatório" era um punhado de KPIs soltos que ninguém lia porque não
// respondiam a pergunta que a gerência faz todo fim de tarde: o que aconteceu
// hoje, quem fez, e o que ficou para trás.
//
// Três blocos, nesta ordem, porque é a ordem da conversa:
//   1. O que ACONTECEU  — contatos, aceites, fichas, ativações
//   2. QUEM fez         — por pessoa, com meta e proporção comprovada
//   3. O que FICOU      — parados, a conferir, documentos, pesquisa vencida
//
// Tudo derivado da trilha. Nenhum número aqui é digitado por alguém.

const noDia = (data, dia) => String(data || "").slice(0, 10) === dia;

/** Eventos da trilha dos parceiros que aconteceram no dia. */
function eventosDoDia(partners = [], dia) {
  const eventos = [];
  for (const p of partners) {
    for (const h of Array.isArray(p.stageHistory) ? p.stageHistory : []) {
      if (!noDia(h?.date, dia)) continue;
      eventos.push({ ...h, empresa: p.name, parceiroId: p.id });
    }
  }
  return eventos;
}

export function montarRelatorio({ partners = [], credenciamentos = [], satisfaction = [], metas = [], dia = getToday() }) {
  const todosContatos = contatos(partners);
  const doDia = contatosNoDia(todosContatos, dia);
  const eventos = eventosDoDia(partners, dia);

  // ── 1. O que aconteceu ──
  const aceites = eventos.filter((e) => e.to === "documentacao" && e.from === "abordagem");
  const novasAbordagens = eventos.filter((e) => e.to === "abordagem" && e.from === "identificado");
  const recusas = eventos.filter((e) => e.tipo === "sem_interesse" || e.tipo === "recusado");
  const justificativas = eventos.filter((e) => e.tipo === "justificativa");
  const avaliacoes = eventos.filter((e) => e.tipo === "avaliacao");
  const avancosAuto = eventos.filter((e) => e.tipo === "avanco_automatico");
  const ativacoes = eventos.filter((e) => e.to === "ativo");

  const fichasCriadas = credenciamentos.filter((c) => noDia(c.createdAt, dia));
  const fichasConcluidas = credenciamentos.filter((c) => (
    (Array.isArray(c.historico) ? c.historico : []).some((h) => h?.acao === "conclusao" && noDia(h.ts, dia))
  ));
  const novasEmpresas = partners.filter((p) => noDia(p.createdAt, dia));

  // ── 2. Quem fez ──
  const pessoas = porPessoa(partners, metas, dia).map((p) => {
    // A meta é por usuário; aqui casamos pelo nome do autor, que é o que a
    // trilha guarda. Sem correspondência, cai na meta geral.
    const dono = partners.find((x) => (x.interactions || []).some((i) => i.autor === p.autor && i.userId));
    const userId = dono ? (dono.interactions.find((i) => i.autor === p.autor) || {}).userId : null;
    const meta = metaDe(metas, userId);
    const feitosHoje = doDia.filter((c) => c.autor === p.autor);
    return {
      autor: p.autor,
      contatos: feitosHoje.length,
      comProva: feitosHoje.filter((c) => c.comprovado).length,
      meta: meta.contatosDia,
      bateu: meta.contatosDia > 0 && feitosHoje.length >= meta.contatosDia,
      aceites: aceites.filter((e) => e.autor === p.autor).length,
      recusas: recusas.filter((e) => e.autor === p.autor).length,
    };
  }).filter((p) => p.contatos > 0 || p.aceites > 0 || p.recusas > 0);

  // ── 3. O que ficou ──
  const parados = partners.filter((p) => precisaJustificar(p, dia))
    .map((p) => ({ nome: p.name, dias: diasParado(p, dia) }))
    .sort((a, b) => b.dias - a.dias);
  const aConferir = partners.reduce((s, p) => s + pendentesDeConferencia(p).length, 0);
  const pesquisaVencida = devidos(partners, satisfaction, dia).length;
  const semProva = doDia.filter((c) => !c.comprovado).length;

  return {
    dia,
    aconteceu: {
      contatos: doDia.length,
      comProva: doDia.filter((c) => c.comprovado).length,
      novasEmpresas: novasEmpresas.length,
      novasAbordagens: novasAbordagens.length,
      aceites: aceites.length,
      recusas: recusas.length,
      justificativas: justificativas.length,
      fichasCriadas: fichasCriadas.length,
      fichasConcluidas: fichasConcluidas.length,
      avancosAuto: avancosAuto.length,
      ativacoes: ativacoes.length,
      avaliacoes: avaliacoes.length,
      empresasAceitaram: aceites.map((e) => e.empresa),
      empresasAtivadas: ativacoes.map((e) => e.empresa),
    },
    pessoas,
    regioes: porRegiao(doDia),
    ficou: { parados, aConferir, pesquisaVencida, semProva },
  };
}

/** O mesmo relatório em texto, para colar no grupo. */
export function relatorioEmTexto(r) {
  const [a, s] = [r.aconteceu, []];
  const dataBr = String(r.dia).split("-").reverse().join("/");
  s.push(`*Resumo do dia — ${dataBr}*`);
  s.push("");
  s.push(`📞 ${a.contatos} ${a.contatos === 1 ? "contato" : "contatos"} registrados (${a.comProva} com print)`);
  if (a.novasEmpresas) s.push(`🏢 ${a.novasEmpresas} ${a.novasEmpresas === 1 ? "empresa nova" : "empresas novas"} no funil`);
  if (a.novasAbordagens) s.push(`🤝 ${a.novasAbordagens} ${a.novasAbordagens === 1 ? "primeira abordagem" : "primeiras abordagens"}`);
  if (a.aceites) s.push(`✅ ${a.aceites} ${a.aceites === 1 ? "aceite" : "aceites"}: ${a.empresasAceitaram.join(", ")}`);
  if (a.fichasConcluidas) s.push(`📄 ${a.fichasConcluidas} ${a.fichasConcluidas === 1 ? "ficha concluída" : "fichas concluídas"}`);
  if (a.ativacoes) s.push(`🟢 ${a.ativacoes} ${a.ativacoes === 1 ? "ativação" : "ativações"}: ${a.empresasAtivadas.join(", ")}`);
  if (a.recusas) s.push(`🚫 ${a.recusas} ${a.recusas === 1 ? "recusa registrada" : "recusas registradas"}`);

  if (r.pessoas.length) {
    s.push("");
    s.push("*Por pessoa*");
    for (const p of r.pessoas) {
      s.push(`• ${p.autor}: ${p.contatos}/${p.meta} ${p.bateu ? "✅" : "⚠️"}${p.comProva < p.contatos ? ` (${p.contatos - p.comProva} sem print)` : ""}${p.aceites ? ` · ${p.aceites} aceite(s)` : ""}`);
    }
  }

  if (r.regioes.length) {
    s.push("");
    s.push("*Onde*");
    for (const g of r.regioes.slice(0, 5)) {
      s.push(`• ${g.uf}: ${g.total} (${g.cidades.slice(0, 3).map((c) => `${c.cidade} ${c.total}`).join(", ")})`);
    }
  }

  const f = r.ficou;
  const pend = [];
  if (f.parados.length) pend.push(`${f.parados.length} parado(s) em abordagem`);
  if (f.aConferir) pend.push(`${f.aConferir} registro(s) a conferir`);
  if (f.semProva) pend.push(`${f.semProva} contato(s) de hoje sem print`);
  if (f.pesquisaVencida) pend.push(`${f.pesquisaVencida} parceiro(s) sem pesquisa há 3 meses`);
  if (pend.length) {
    s.push("");
    s.push("*Ficou para trás*");
    for (const x of pend) s.push(`• ${x}`);
  }

  return s.join("\n");
}
